import {
  Box,
  Stack,
  styled,
  useTheme,
  Typography,
  Divider,
} from "@mui/material";
import React, { useState } from "react";
import useWindowSize from "../../../hooks/useWindowSize";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import Iconify from "../../iconify/iconify";

const StyledMinImgBox = styled(Box)(({ theme }) => ({
  height: "500px",
  paddingRight: "50px",
  borderRadius: "10px",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    height: "220px",
    paddingRight: "0px",
  },
}));

const StyledIndexBox = styled(Box)(({ theme }) => ({
  width: "15px",
  height: "5px",
  borderRadius: "50px",
  cursor: "pointer",
  background: theme.palette.background.light,
  "&.active": {
    background: "#a95052",
  },
}));

const StyledArrowBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  padding: "10px",
  border: `1px solid ${theme.palette.text.secondary}`,
  color: theme.palette.text.secondary,
  cursor: "pointer",
}));

const ModelsCarousel = ({ modelsData }) => {
  const theme = useTheme();
  const screenWidth = useWindowSize();
  const [swiper, setSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleIndexClick = (index) => {
    if (swiper) {
      swiper.slideTo(index);
      setActiveIndex(index);
    }
  };

  const handleSlideChange = () => {
    if (swiper) {
      setActiveIndex(swiper.realIndex);
    }
  };

  const handleSwiperInit = (swiper) => {
    setSwiper(swiper);
  };

  const handlePrevButton = () => {
    if (swiper) {
      swiper.slidePrev();
    }
  };

  const handleNextButton = () => {
    if (swiper) {
      swiper.slideNext();
    }
  };

  return modelsData.length > 0 ? (
    <Stack
      p={{ xs: "25px", md: "40px" }}
      mb="30px"
      bgcolor={theme.palette.background.light}
      borderRadius="10px"
      width={{ lg: "93%", xl: "80%" }}
      sx={{
        margin: "0 auto",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        display={{ xs: "none", sm: "block" }}
        alignItems={{ xs: "start", md: "center" }}
        gap={2}
      >
        <Typography variant="h3" color="text.secondary">
          MODELOS
        </Typography>
        <Divider
          variant="fullWidth"
          orientation="horizontal"
          sx={{ borderColor: "#a95052", width: "100%" }}
        />
      </Stack>
      <Stack
        direction="row"
        pt={{ xs: "0px", sm: "20px" }}
        pl={{ xs: "0px", sm: "20px", md: "40px" }}
        height="100%"
      >
        <Swiper
          grabCursor={true}
          centeredSlides={false}
          slidesPerView={1}
          spaceBetween={10}
          coverflowEffect={{
            rotate: 50,
            stretch: 0,
            depth: 100,
            modifier: 2,
            slideShadows: false,
          }}
          loop={true}
          className="mySwiper"
          onSlideChange={handleSlideChange}
          onSwiper={handleSwiperInit}
          modules={[Navigation, Pagination]}
        >
          {modelsData?.map((model, index) => {
            return (
              <SwiperSlide key={index} height="auto">
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "center", md: "start" }}
                  gap={{ xs: 2, md: 10 }}
                >
                  <Stack width={{ xs: "100%", md: "37%" }}>
                    <Typography variant="h4" color="text.secondary">
                      {model.modelData.title}
                    </Typography>
                    <Typography variant="h2" color="text.secondary">
                      {model.modelData.name}
                    </Typography>
                    <Divider
                      variant="fullWidth"
                      orientation="horizontal"
                      sx={{
                        borderColor: "#a95052",
                        width: "13%",
                        paddingY: "5px",
                      }}
                    />
                    <Typography
                      variant="h4"
                      color="text.secondary"
                      py={{ xs: "10px", md: "20px" }}
                    >
                      Superficie: {model.surface}
                    </Typography>
                    {model.modelData.properties?.map((text, index) => (
                      <Box
                        display="flex"
                        pl="20px"
                        pb={{ xs: "0px", md: "12px" }}
                        gap={1}
                        alignItems="center"
                      >
                        <Iconify icon="mdi:check" style={{ color: "white" }} />
                        <Typography variant="h5" color="text.secondary">
                          {text}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Stack direction={{ xs: "column", md: "row" }}>
                    {model.images.map((value, index) => {
                      if (index === 0) {
                        return (
                          <StyledMinImgBox>
                            <Box
                              component="img"
                              width="100%"
                              height={{ xs: "100%", md: "95%" }}
                              src={value}
                              alt={`slide-${index}`}
                            />
                          </StyledMinImgBox>
                        );
                      }

                      return (
                        <StyledMinImgBox
                          sx={{
                            display: {
                              xs: "none",
                              md: !!value ? "block" : "none",
                            },
                          }}
                        >
                          <Box
                            component="img"
                            width="100%"
                            height={{ xs: "100%", md: "90%" }}
                            src={value}
                            alt={`slide-${index}-2`}
                          />
                        </StyledMinImgBox>
                      );
                    })}
                  </Stack>
                </Stack>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Stack>
      <Stack
        direction="row"
        justifyContent="center"
        mt={{ xs: 2, sm: 5, lg: 0, xl: 5 }}
      >
        <Box
          padding="10px"
          borderRadius="50px"
          bgcolor={theme.palette.background.default}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <StyledArrowBox onClick={handlePrevButton}>
              <Iconify icon="grommet-icons:link-previous" />
            </StyledArrowBox>
            {modelsData
              ?.slice(0, screenWidth <= 600 ? 10 : modelsData.length)
              .map((_, index) => (
                <StyledIndexBox
                  key={index}
                  onClick={() => handleIndexClick(index)}
                  className={activeIndex === index ? "active" : ""}
                />
              ))}
            <StyledArrowBox onClick={handleNextButton}>
              <Iconify icon="ooui:arrow-previous-rtl" />
            </StyledArrowBox>
          </Stack>
        </Box>
      </Stack>
    </Stack>
  ) : (
    <></>
  );
};

export default ModelsCarousel;
